/**
 * chart-utils.ts — the pure geometry of the ported SVG charts (spec-business §6, docs/dataviz).
 *
 * Every chart draws in a coordinate system the size of its container (1 unit = 1 CSS px), so
 * 12px axis text is 12px on a 390px phone and inside a 12-column desktop section alike.
 * These helpers are pure and unit-tested (`web/src/test/chart-utils.test.ts`).
 */

export interface Ticks {
  ticks: number[];
  min: number;
  max: number;
  step: number;
}

/**
 * "Nice" round ticks over `[min, max]` — the mockup's `niceTicks`, which is what produces the
 * spec's "0 / 10K / 20K" axis. Works for zero-based and diverging (negative) domains alike.
 */
export function niceTicks(min: number, max: number, count = 4): Ticks {
  let hi = max;
  if (hi === min) hi = min + 1;
  const span = hi - min;
  const rough = span / count;
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const candidates = [1, 2, 2.5, 5, 10].map((m) => m * pow);
  const step = candidates.find((s) => span / s <= count) ?? candidates[candidates.length - 1] ?? 1;
  const lo = Math.floor(min / step) * step;
  const top = Math.ceil(hi / step) * step;
  const ticks: number[] = [];
  for (let v = lo; v <= top + 1e-9; v += step) ticks.push(Math.round(v));
  return { ticks, min: lo, max: top, step };
}

/** A column with a 4px-rounded data end and a square baseline (dataviz marks spec). */
export function topRoundedRect(x: number, y: number, w: number, h: number, r: number): string {
  if (h <= 0) return '';
  const radius = Math.min(r, w / 2, h);
  const f = (n: number) => Number(n.toFixed(2));
  return `M${f(x)} ${f(y + h)}V${f(y + radius)}a${f(radius)} ${f(radius)} 0 0 1 ${f(radius)} ${f(-radius)}h${f(w - 2 * radius)}a${f(radius)} ${f(radius)} 0 0 1 ${f(radius)} ${f(radius)}V${f(y + h)}z`;
}

/** The mirror of `topRoundedRect` — the "יצא" half of a diverging column (spec §6 C4). */
export function bottomRoundedRect(x: number, y: number, w: number, h: number, r: number): string {
  if (h <= 0) return '';
  const radius = Math.min(r, w / 2, h);
  const f = (n: number) => Number(n.toFixed(2));
  return `M${f(x)} ${f(y)}V${f(y + h - radius)}a${f(radius)} ${f(radius)} 0 0 0 ${f(radius)} ${f(radius)}h${f(w - 2 * radius)}a${f(radius)} ${f(radius)} 0 0 0 ${f(radius)} ${f(-radius)}V${f(y)}z`;
}

/** A polyline `d` through points. */
export function linePath(points: ReadonlyArray<readonly [number, number]>): string {
  return points.map(([x, y], i) => `${i ? 'L' : 'M'}${x.toFixed(1)} ${y.toFixed(1)}`).join('');
}

/** The area under a line, closed on `baselineY`. */
export function areaPath(points: ReadonlyArray<readonly [number, number]>, baselineY: number): string {
  if (!points.length) return '';
  const first = points[0]!;
  const last = points[points.length - 1]!;
  return `${linePath(points)}L${last[0].toFixed(1)} ${baselineY.toFixed(1)}L${first[0].toFixed(1)} ${baselineY.toFixed(1)}z`;
}

/** Evenly spaced band centres for `n` marks inside `[left, right]`. */
export function bandScale(n: number, left: number, right: number): { band: number; center: (index: number) => number } {
  const band = n > 0 ? (right - left) / n : 0;
  return { band, center: (index: number) => left + band * index + band / 2 };
}

/** Linear scale from a value domain to a pixel range (y axes are inverted by passing rangeMax < rangeMin). */
export function linearScale(domainMin: number, domainMax: number, rangeMin: number, rangeMax: number): (value: number) => number {
  const span = domainMax - domainMin || 1;
  return (value: number) => rangeMin + ((value - domainMin) / span) * (rangeMax - rangeMin);
}

export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, value));
}

/** The ordinal ramp class of an aging bucket (spec §6 "רמפה סדורה", 4 steps). */
export function ordinalClass(index: number): string {
  return `o${clamp(index + 1, 1, 4)}`;
}
